// src/Reports.js
import React, { useState } from "react";
import dataStore from "./dataStore";
import TableView from "./TableView";
import { toCSV, downloadCsv } from "./utils/csv";

// Canonical header order per dataset (also used to export template CSVs)
const TEMPLATE_HEADERS = {
  farmers: [
    "full_name","gender","birth_year","phone","whatsapp","email",
    "country","region","district","village","gps_lat","gps_lng",
    "primary_language","consent_to_data_use","aggregator_id",
    "verified","created_at","farmer_id","verified_by","verified_at"
  ],
  plots: [
    "plot_id","farmer_id","plot_name","gps_lat","gps_lng","area_ha",
    "land_use","soil_type","irrigation","elevation_m","slope_deg",
    "verified","verified_by","verified_at","created_at"
  ],
  crops: [
    "crop_entry_id","plot_id","season_year","season_name","crop_species",
    "variety","planting_date","harvest_date","yield_kg","market_channel",
    "price_local_per_kg","verified","verified_by","verified_at","created_at"
  ],
  biochar: [
    "application_id","plot_id","date_applied","feedstock_type","kiln_type",
    "biochar_mass_kg","application_rate_t_per_ha","incorporation_method",
    "co_composted","estimated_fixed_C_fraction","notes",
    "verified","verified_by","verified_at","created_at"
  ],
  soil: [
    "sample_id","plot_id","sample_date","sample_depth_cm",
    "soil_organic_carbon_pct","bulk_density_g_cm3","ph","moisture_pct",
    "lab_id","method_ref","verified","verified_by","verified_at","created_at"
  ],
};

export default function Reports() {
  const [active, setActive] = useState("farmers");
  const [query, setQuery] = useState("");
  const [, forceRefresh] = useState(0); // only used to re-render after mutations/imports

  // Read current data each render (small datasets; keeps logic simple & lint quiet)
  const farmers = dataStore.list("farmers");
  const plots   = dataStore.list("plots");
  const crops   = dataStore.list("crops");
  const biochar = dataStore.list("biochar_applications");
  const soil    = dataStore.list("mrv_samples");

  const kpis = [
    { label: "Farmers", value: farmers.length },
    { label: "Plots", value: plots.length },
    { label: "Crops", value: crops.length },
    { label: "Biochar Apps", value: biochar.length },
    { label: "Soil Samples", value: soil.length },
  ];

  const datasets = {
    farmers: { title: "Farmers", rows: farmers },
    plots:   { title: "Plots", rows: plots },
    crops:   { title: "Crops", rows: crops },
    biochar: { title: "Biochar Applications", rows: biochar },
    soil:    { title: "Soil Samples (MRV)", rows: soil },
  };
  const current = datasets[active] || datasets.farmers;

  // Filter across visible columns
  const q = query.trim().toLowerCase();
  const filteredRows = q
    ? (current.rows || []).filter((r) =>
        Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q))
      )
    : current.rows;

  // Export: rows if present, otherwise header-only template
  const exportCsv = () => {
    const rows = filteredRows || [];
    const canonical = TEMPLATE_HEADERS[active] || [];

    let headers;
    if (rows.length) {
      const present = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
      const extras = present.filter((h) => !canonical.includes(h));
      headers = canonical.length
        ? [...canonical.filter((h) => present.includes(h)), ...extras]
        : present;
    } else {
      headers = canonical;
    }

    if (!headers.length) {
      alert("No rows to export and no template is defined for this tab.");
      return;
    }
    const csv = toCSV(headers, rows);
    const fname = current.title.replace(/\s+/g, "_").toLowerCase();
    downloadCsv(`${fname}.csv`, csv);
  };

  return (
    <div className="container">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h1 style={{ margin: 0 }}>AGR Reports</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {Object.keys(datasets).map((key) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #ddd",
                background: active === key ? "#eef7ff" : "#fff",
              }}
            >
              {datasets[key].title}
            </button>
          ))}
          <button onClick={() => forceRefresh((n) => n + 1)}>Refresh</button>
        </div>
      </header>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 12 }}>
        {kpis.map((k) => (
          <div key={k.label} className="card" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#666" }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="card" style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ marginRight: 8 }}>{current.title}</strong>
        <input
          type="search"
          placeholder="Filter rows…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <button onClick={exportCsv}>Export CSV (filtered)</button>
      </div>

      {/* Table */}
      <TableView
        title={current.title}
        rows={filteredRows}
        showExport={false}
        columns={TEMPLATE_HEADERS[active] || []}
      />
    </div>
  );
}